import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "@core/i18n";
import {
  Sparkles,
  Search,
  Users as UsersIcon,
  Check,
  X,
  FlaskConical,
} from "lucide-react";
import {
  pulseMetricsService,
  PulseUserRow,
} from "@core/services/PulseMetricsService";

const PAGE_SIZE = 50;

const formatDate = (iso: string | null): string => {
  if (!iso) return "—";
  return new Intl.DateTimeFormat(i18n.language || "fr", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  }).format(new Date(iso));
};

const planColor = (plan: string, lsStatus: string | null): string => {
  if (plan === "free") return "bg-gray-500/20 text-gray-300";
  if (lsStatus === "active") return "bg-green-500/20 text-green-300";
  if (lsStatus === "cancelled" || lsStatus === "past_due")
    return "bg-red-500/20 text-red-300";
  return "bg-blue-500/20 text-blue-300";
};

export const UsersAdmin: React.FC = () => {
  const { t } = useTranslation("forms");
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState<PulseUserRow[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState(searchParams.get("search") ?? "");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { total, rows } = await pulseMetricsService.getUsers(
        search.trim() || null,
        PAGE_SIZE,
        page * PAGE_SIZE,
      );
      setRows(rows);
      setTotal(total);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("admin.users.error_fallback"),
      );
    } finally {
      setLoading(false);
    }
  }, [search, page, t]);

  useEffect(() => {
    const h = setTimeout(load, 300);
    return () => clearTimeout(h);
  }, [load]);

  const toggleTestFlag = async (row: PulseUserRow) => {
    try {
      await pulseMetricsService.setUserTestFlag(row.id, !row.is_test);
      setRows((prev) =>
        prev.map((r) =>
          r.id === row.id ? { ...r, is_test: !row.is_test } : r,
        ),
      );
    } catch (err) {
      alert(
        err instanceof Error ? err.message : t("admin.users.error_fallback"),
      );
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="min-h-screen-safe bg-[#030712] text-white">
      <header className="relative z-10 border-b border-white/5 bg-black/20 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-8 h-8 rounded bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <span className="text-md font-semibold">DooQuest Admin</span>
            </Link>
            <nav className="flex items-center gap-4">
              <Link
                to="/admin"
                className="text-xs text-gray-400 hover:text-white"
              >
                Support
              </Link>
              <Link
                to="/admin/roadmap"
                className="text-xs text-gray-400 hover:text-white"
              >
                Roadmap
              </Link>
              <Link
                to="/admin/analytics"
                className="text-xs text-gray-400 hover:text-white"
              >
                Analytics
              </Link>
              <Link
                to="/admin/pulse"
                className="text-xs text-gray-400 hover:text-white"
              >
                Pulse
              </Link>
              <Link
                to="/admin/users"
                className="text-xs text-white font-medium"
              >
                Users
              </Link>
              <Link to="/" className="text-xs text-gray-500 hover:text-white">
                {t("admin.logout")}
              </Link>
            </nav>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center gap-3 mb-6">
          <UsersIcon className="w-6 h-6 text-blue-400" />
          <div>
            <h1 className="text-2xl font-semibold">{t("admin.users.title")}</h1>
            <p className="text-sm text-gray-400">
              {t("admin.users.resultsCount", {
                count: new Intl.NumberFormat(i18n.language || "fr").format(
                  total,
                ),
                plural: total > 1 ? "s" : "",
              })}
              {search ? t("admin.users.forQuery", { query: search }) : ""}
            </p>
          </div>
        </div>

        <div className="mb-4 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder={t("admin.users.searchPlaceholder")}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
              const v = e.target.value.trim();
              if (v) setSearchParams({ search: v });
              else setSearchParams({});
            }}
            className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
          />
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded text-sm text-red-300">
            {error}
          </div>
        )}

        <div className="bg-white/5 border border-white/10 rounded-xl overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10 bg-white/5 text-xs uppercase tracking-wide text-gray-400">
                <th className="px-4 py-2 text-left font-medium">
                  {t("admin.users.colEmail")}
                </th>
                <th className="px-4 py-2 text-left font-medium w-24">
                  {t("admin.users.colPlan")}
                </th>
                <th className="px-4 py-2 text-right font-medium w-20">
                  {t("admin.users.colPolls")}
                </th>
                <th className="px-4 py-2 text-left font-medium w-28">
                  {t("admin.users.colSignedUp")}
                </th>
                <th className="px-4 py-2 text-left font-medium w-28">
                  {t("admin.users.colLastLogin")}
                </th>
                <th className="px-4 py-2 text-center font-medium w-20">
                  {t("admin.users.colTest")}
                </th>
                <th className="px-4 py-2 text-right font-medium w-24">
                  {t("admin.users.colActions")}
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-8 text-center text-gray-500"
                  >
                    {t("admin.users.loading")}
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-8 text-center text-gray-500"
                  >
                    {t("admin.users.empty")}
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr
                    key={r.id}
                    className={`border-b border-white/5 hover:bg-white/5 ${r.is_test ? "opacity-60" : ""}`}
                  >
                    <td className="px-4 py-2 text-sm text-white font-mono">
                      {r.email}
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={`text-xs px-2 py-0.5 rounded ${planColor(r.plan_type, r.lemonsqueezy_status)}`}
                      >
                        {r.plan_type}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right text-sm text-gray-300">
                      {r.polls_count}
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-400">
                      {formatDate(r.created_at)}
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-400">
                      {formatDate(r.last_sign_in_at)}
                    </td>
                    <td className="px-4 py-2 text-center">
                      {r.is_test ? (
                        <FlaskConical className="w-4 h-4 text-amber-400 mx-auto" />
                      ) : (
                        <span className="text-gray-600">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={() => toggleTestFlag(r)}
                        className="text-xs px-2 py-1 bg-white/5 border border-white/10 rounded hover:bg-white/10 flex items-center gap-1 ml-auto"
                        title={
                          r.is_test
                            ? t("admin.users.removeTestFlag")
                            : t("admin.users.markAsTest")
                        }
                      >
                        {r.is_test ? (
                          <>
                            <X className="w-3 h-3" /> un-test
                          </>
                        ) : (
                          <>
                            <Check className="w-3 h-3" /> tag test
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between">
            <div className="text-xs text-gray-500">
              {t("admin.users.pageOf", { page: page + 1, total: totalPages })}
            </div>
            <div className="flex gap-2">
              <button
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="text-xs px-3 py-1 bg-white/5 border border-white/10 rounded hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                {t("admin.users.prev")}
              </button>
              <button
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => p + 1)}
                className="text-xs px-3 py-1 bg-white/5 border border-white/10 rounded hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                {t("admin.users.next")}
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default UsersAdmin;
